<template>
    <div class="aiAssistantChatContainer" ref="chatContainer">
        <!-- Header doubles as the drag handle (.header) — keep that class. -->
        <div class="header aiHeader" ref="header">
            <span class="aiHeader__avatar">
                <img src="../../images/BotIcon48.png" alt="">
            </span>

            <span class="aiHeader__title">Asistente de IA</span>

            <button type="button" class="aiIconBtn aiHeader__close" @click="CloseAiAssistant()"
                aria-label="Cerrar asistente" title="Cerrar">
                <i class="bi bi-x-lg"></i>
            </button>
        </div>

        <div class="helpButtonWrapper" v-show="messages.length > 0">
            <button class="helpButton" @click="ToggleInstructions"
                :aria-label="showInstructions ? 'Volver a la conversación' : '¿Cómo funciona el asistente?'"
                :title="showInstructions ? 'Volver a la conversación' : '¿Cómo funciona el asistente?'">
                <i :class="{'bi bi-question-lg' : !showInstructions, 'bi bi-arrow-left' : showInstructions }"></i>
            </button>
        </div>

        <div class="body" ref="body" v-show="!showInstructions && (messages.length > 0 || firstChatLoad)">
            <div class="fullWidth aiAssistantCentered assistantLoadingWrapper" :hidden="endOfChat">
                <div ref="loading" class="spinner-border assistantLoading"></div>
            </div>

            <AssistantMessage v-for="message in messages" :key="message" :message="message" />
            <AssistantMessage v-show="loading" :message="loadingMessage" :loadingMessage="true" />

            <div ref="chatBottom" class="hiddenObserverDiv"></div>
        </div>

        <div ref="instructionsBody" class="body instructions" v-show="showInstructions || (messages.length == 0 && !firstChatLoad)">
            <div class="instructionsBotRow aiAssistantCentered">
                <div class="instructionsBotIconWrapper aiAssistantCentered">
                    <img src="../../images/BotIcon48.png">
                </div>
            </div>

            <div class="instructionsCard">
                <div class="instructionsHeader">
                    <span class="instructionsHeader__icon"><i class="bi bi-info-circle"></i></span>
                    ¿Cómo funciona el asistente?
                </div>
                ¡Bienvenido! El Asistente de Inteligencia Artificial está aquí para <b>ayudarte a resolver
                    dudas y manejar tareas de manera eficiente.</b><br/>
                Puedes interactuar con él para recibir asistencia en tiempo real, ya sea para preguntas generales o para
                problemas específicos que enfrentes.
            </div>

            <div class="instructionsCard">
                <div class="instructionsHeader">
                    <span class="instructionsHeader__icon"><i class="bi bi-chat-dots"></i></span>
                    ¿Cómo comunicarme con el asistente?
                </div>
                Para comunicarte con el asistente, simplemente escribe tu mensaje en el cuadro de texto y da click en el
                botón de enviar.<br/>
                Trata de ser lo más <b>claro y específico</b> posible para obtener respuestas precisas.
            </div>

            <div class="instructionsCard">
                <div class="instructionsHeader">
                    <span class="instructionsHeader__icon"><i class="bi bi-paperclip"></i></span>
                    El asistente puede analizar archivos
                </div>
                Puedes adjuntar archivos junto a tus mensajes para que sean <b>analizados por el asistente</b>.<br/>
                El asistente puede analizar <b>imagenes, documentos de Word, PDFs y archivos de texto</b>.<br/>
                Toma en cuenta que las imágenes dentro de documentos Word y PDF no pueden ser analizadas.
            </div>
        </div>

        <transition name="fade">
            <div ref="goToBottom" class="aiAssistantCentered fullWidth goToBottomBurronWrapper" v-show="messages.length > 0 && !isViewingBottom && !showInstructions">
                <button class="goToBottomButton" @click="GoToBottom" aria-label="Ir al final de la conversación" title="Ir al final">
                    <i class="bi bi-arrow-down"></i>
                </button>
            </div>
        </transition>
        <div class="footer" v-show="(!showInstructions && messages.length > 0) || messages.length == 0">
            <div class="messageBar" ref="messageBar">
                <div class="filesContainer" :class="{ 'empty' : files.length == 0 }">
                    <button v-if="files.length > 0" @click="ClearAttachments" class="aiIconBtn clearFilesButton"
                        tabindex="-1" title="Deseleccionar todos" aria-label="Deseleccionar todos los archivos">
                        <i class="bi bi-x-lg"></i>
                    </button>
                    <div class="selectedFile" v-for="file in files" :key="file.name">
                        <FileTypeIcon :type="file.type" :size="18" class="selectedFile__icon" />
                        <span class="selectedFile__name">{{ file.name }}</span>
                        <button class="removeFileButton aiAssistantCentered" @click="RemoveFile(file)"
                            :aria-label="'Quitar ' + file.name" title="Quitar archivo">
                            <i class="bi bi-x"></i>
                        </button>
                    </div>

                    <input type="file" ref="fileInput" multiple hidden @change="AttachmentsChangeHandler" />
                </div>
                <div v-if="maxAllowedCharactersExceeded" class="warningsWrapper">
                    <label class="required">Cantidad máxima de caracteres permitidos: {{ maxAllowedCharacters }}</label>
                </div>
                <div class="messageInputWrapper">
                    <div class="buttonWrapper">
                        <button class="aiIconBtn" tabindex="-1" @click="OpenAttachments" title="Adjuntar archivos"
                            aria-label="Adjuntar archivos"
                            :disabled="files.length >= maxAmountOfFiles">
                            <i class="bi bi-paperclip"></i>
                        </button>
                    </div>
                    <div class="textAreaWrapper">
                        <textarea ref="messageInput" v-model="messageValue" id="auto-growing-textarea"
                            placeholder="Envía un mensaje al asistente" aria-label="Mensaje para el asistente">
                        </textarea>
                    </div>
                    <div class="buttonWrapper">
                        <button class="aiIconBtn postMessageButton" tabindex="-1" @click="AddAssistantMessage"
                            :title="messageValueLength == 0 ? 'Escribe un mensaje para poder enviarlo' : 'Enviar mensaje'"
                            aria-label="Enviar mensaje"
                            :disabled="sendMessageDisabled"
                            :class="{'disabled' : messageValueLength == 0 || loading}">
                            <i class="bi bi-arrow-up"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AssistantService from '@/services/AssistantService';
import ToastService from '@/services/ToastService';
import AssistantMessage from './AssistantMessage.vue';
import FileTypeIcon from '@/components/Generic/FileTypeIcon.vue';
import { useStore } from '@/store/DefaultStore.js';

export default {
    name: 'AiAssistant',
    components: {
        AssistantMessage,
        FileTypeIcon
    },
    setup(){
        const store = useStore();

        return {
            store
        }
    },
    data() {
        return {
            showInstructions: false,
            messageValue: '',
            loading: false,
            messages: [],
            chatObserver: null,
            headerObserver: null,
            instructionsObserver: null,
            assistantObserver: null,
            chatHeight: 0,

            chatBottomObserver: null,
            isViewingBottom: true,

            maxFileSizeInMb: 100,
            maxAmountOfFiles: 10,

            loadingObserver: null,
            endOfChat: false,
            loadingChat: true,
            firstChatLoad: true,

            maxAllowedCharacters: 50000,
            files: [],
            allowedMimeTypes: {
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : {
                    isImage: false,
                    'class' : 'Word'
                },
                'application/pdf' : {
                    isImage: false,
                    'class' : 'PDF'
                },
                'text/plain' : {
                    isImage: false,
                    'class' : 'Text'
                },
                'image/jpeg' : {
                    isImage: true,
                    'class' : 'Image'
                },
                'image/png' : {
                    isImage: true,
                    'class' : 'Image'
                }
            },

            loadingMessage : {
                content: `<div class="loading-dots aiAssistantCentered">
                    <div></div>
                    <div></div>
                    <div></div>
                    </div>`,
                isUserMessage: false,
                files: []
            }
        };
    },

    computed: {
        messageValueLength() {
            return this.messageValue.trim().length;
        },

        maxAllowedCharactersExceeded() {
            return this.messageValue.trim().length > this.maxAllowedCharacters;
        },

        sendMessageDisabled() {
            return this.loading || this.messageValueLength == 0 || this.maxAllowedCharactersExceeded;
        }
    },

    mounted() {
        // Observer to resize the height of the text area when writting on it
        const textarea = document.getElementById('auto-growing-textarea');
        textarea.addEventListener('input', function() {
            let isMaxed = this.scrollHeight > parseInt(window.getComputedStyle(this).getPropertyValue('max-height'), 10);
            if (isMaxed) {
                return;
            }

            this.style.height = '40px';
            this.style.height = this.scrollHeight + 'px';
        });

        // Event to trigger Send Message when enter pressed
        let AddAssistantMessage = this.AddAssistantMessage;
        document.getElementById('auto-growing-textarea').addEventListener('keydown', function(event) {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                AddAssistantMessage();
            }
        });

        // Observer that resize the body when the message bar changes height
        let observer = this.GetResizeObserver(this.RecalculateBodyHeight);

        this.headerObserver = observer;
        this.chatObserver = observer;

        this.headerObserver.observe(this.$refs.header);
        this.chatObserver.observe(this.$refs.messageBar);

        // Observer that check if the user is viewing the bottom of the chat (used for auto scroll)
        this.chatBottomObserver = this.GetIsViewedObserver((isViewingBottom) => {
            this.isViewingBottom = isViewingBottom;
        });
        this.chatBottomObserver.observe(this.$refs.chatBottom);

        // Observer to load more messages when the user reaches the top of the chat
        let container = this.$refs.body;
        container.addEventListener('scroll', this.GetScrollObserver());
        this.loadingObserver = this.GetIsViewedObserver(async (isIntersecting) => {
            if(isIntersecting){
                await this.GetConversation(!this.firstChatLoad);

                // Restart the observer
                this.loadingObserver.disconnect();

                if(!this.endOfChat){
                    setTimeout(() => {
                        if(this.$refs.loading){
                            this.loadingObserver.observe(this.$refs.loading);
                        }
                    }, 1000);
                }
            }
        });
        this.loadingObserver.observe(this.$refs.loading);

        // Mobile only
        if(!this.store.isMobile){
            return;
        }

         // Observer that checks if the assistant is being viewed
         let options = {
            threshold: 0.01
        };
        this.assistantObserver = this.GetIsViewedObserver((isViewingAssistant) => {
            if(!isViewingAssistant && !this.firstChatLoad){
                this.ResetSize();
            }
        }, options);
        //Set the options to only work if completely dissapears
        this.assistantObserver.observe(this.$refs.chatContainer, options);

        this.mobileHeaderObserver = this.GetIsViewedObserver((isViewingHeader) => {
            if(!isViewingHeader && !this.firstChatLoad){
                this.ResetSize();
            }
        }, options);
        //Set the options to only work if completely dissapears
        this.assistantObserver.observe(this.$refs.header, options);
    },

    beforeUnmount() {
        this.chatObserver.disconnect();
        this.chatBottomObserver.disconnect();
        this.loadingObserver.disconnect();
        this.headerObserver.disconnect();

        if(this.store.isMobile){
            this.assistantObserver.disconnect();
            this.mobileHeaderObserver.disconnect();
        }
    },

    methods: {
        ToggleInstructions() {
            this.showInstructions = !this.showInstructions;
        },

        GoToBottom() {
            this.ScrollToBottom(true);
        },

        RecalculateBodyHeight() {
            let messageBar = this.$refs.messageBar.clientHeight;
            let headerHeight = this.$refs.header.clientHeight;

            // 20 px because of padding and margin
            this.$refs.body.style.height = `calc(100% - ${messageBar}px - ${headerHeight}px - 20px)`;
            // Applies it to the instructions body too
            this.$refs.instructionsBody.style.height = `calc(100% - ${messageBar}px - ${headerHeight}px)`;

            let goToBottomButton = this.$refs.goToBottom;
            goToBottomButton.style.bottom = `${messageBar + 15}px`;

            // Scroll to the bottom of the chat (only if the user is viewing the bottom)
            if(this.isViewingBottom){
                this.ScrollToBottom();
            }
        },

        DisconnectScrollObserver() {
            let element = this.$refs.body;
            element.removeEventListener('scroll', this.GetScrollObserver(element));
        },

        GetScrollObserver() {
            let container = this.$refs.body;
            return () => {
                const scrollTop = container.scrollTop;
                const scrollThreshold = 500;

                if (scrollTop <= scrollThreshold) {
                    this.GetConversation(true);
                }
            };
        },

        ScrollToBottom(animate = false) {
            let behavior = animate ? 'smooth' : 'auto';

            this.$refs.body.scrollTo({
                top: this.$refs.body.scrollHeight,
                behavior: behavior
            });
        },

        ClearAttachments() {
            this.files = [];
        },

        GetIsViewedObserver(method, options = { threshold: 0.5 }) {
            return new IntersectionObserver(entries => {
                for (let entry of entries) {
                    method(entry.isIntersecting);
                }
            }, options);
        },

        GetResizeObserver(method) {
            return new ResizeObserver(entries => {
                for (let entry of entries) {
                    const height = entry.contentRect.height;
                    method(height);
                }
            });
        },

        RemoveFile(file) {
            this.files = this.files.filter(f => f !== file);
        },

        AttachmentsChangeHandler(event) {
            let selectedFiles = event.target.files;

            for (let i = 0; i < selectedFiles.length; i++) {
                if(this.files.length >= this.maxAmountOfFiles){
                    ToastService.Error('NO SE PUEDEN AGREGAR MÁS DE ' + this.maxAmountOfFiles + ' ARCHIVOS');
                    break;
                }

                if(!this.allowedMimeTypes[selectedFiles[i].type]){
                    ToastService.Error('EL TIPO DE ARCHIVO DEL DOCUMENTO ' + selectedFiles[i].name + ' NO ES SOPORTADO');
                    continue;
                }

                if(selectedFiles[i].size > this.maxFileSizeInMb * 1024 * 1024){
                    ToastService.Error('EL TAMAÑO DEL DOCUMENTO ' + selectedFiles[i].name + ' SUPERA EL LÍMITE DE ' + this.maxFileSizeInMb + 'MB');
                    continue;
                }

                let isImage = this.allowedMimeTypes[selectedFiles[i].type].isImage;
                if(this.files.length > 0 && this.allowedMimeTypes[this.files[0].type].isImage !== isImage){
                    ToastService.Error('EL ANALISIS DE DOCUMENTOS NO ES COMPATIBLE CON EL ANALISIS DE IMAGENES, POR FAVOR SELECCIONE UN SOLO TIPO DE ARCHIVO');
                    continue;
                }

                this.files.unshift(selectedFiles[i]);
            }

            this.$refs.fileInput.value = '';
        },

        OpenAttachments() {
            this.$refs.fileInput.click();
        },

        ResetSize() {
            this.$parent.$parent.ResetAssistant(false);
        },

        CloseAiAssistant() {
            this.$parent.$parent.ToggleAiAssistant(false);
        },

        async GetConversation(keepScrollPosition = false) {
            if((this.loadingChat && !this.firstChatLoad) || this.endOfChat){
                return;
            }

            // Gets the following 25 messages
            let lastMessageId = this.messages.length > 0 ? this.messages[0].id : 0;
            this.loadingChat = true;

            await AssistantService.GetAssistantConversation({ lastMessageId: lastMessageId })
                .then(response => {
                    if(response.length < 25) {
                        this.endOfChat = true;
                        this.DisconnectScrollObserver();
                    }

                    let scrollHeight;
                    if(keepScrollPosition){
                        let container = this.$refs.body;
                        scrollHeight = container.scrollHeight;
                    }

                    // Loaded in bulk, so they shouldn't replay the entrance animation
                    response.forEach(message => message.historical = true);

                    // Unshift the messages to the beginning of the array
                    this.messages.unshift(...response);

                    this.$nextTick(() => {
                        if(this.firstChatLoad){
                            this.ScrollToBottom();
                            this.firstChatLoad = false;
                        }

                        if(keepScrollPosition){
                            let container = this.$refs.body;
                            container.scrollTop = container.scrollHeight - scrollHeight;
                        }
                    });
                })
                .catch(error => {
                    ToastService.Error(error, 'OCURRIÓ UN ERROR AL CARGAR LA CONVERSACIÓN CON EL ASISTENTE');
                })
                .finally(() => {
                    this.loadingChat = false;
                });
        },

        AddAssistantMessage(){
            // So only one message can be processed at a time
            if(this.sendMessageDisabled){
                return;
            }

            this.loading = true;

            // Set the file to be sent
            let formData = new FormData();
            this.files.forEach(file => {
                formData.append('files[]', file);
            });

            let scrollToBottom = this.ScrollToBottom;

            let filesToDisplay = this.files.map(x => {
                return {
                    name: x.name,
                    type: this.allowedMimeTypes[x.type].class
                }
            });

            // Add the user message to the chat
            this.messages.push({
                content: this.messageValue,
                isUserMessage: true,
                files: filesToDisplay
            });

            let dataToSend = {
                message: this.messageValue,
            }
            // Clear the message input
            this.messageValue = '';
            // Reset the height of the text area
            this.$refs.messageInput.style.height = '40px';
            // Clear any attached files now that they are on their way
            let sentFiles = this.files;
            this.files = [];

            // If seeing last message, scroll to the bottom
            if(scrollToBottom){
                this.$nextTick(() => {
                    this.ScrollToBottom();
                });
            }

            AssistantService.AddAssistantMessage(dataToSend, formData)
                .then(response => {
                    response.isUserMessage = false;
                    response.files = [];
                    this.messages.push(response);

                    if(scrollToBottom){
                        this.$nextTick(() => {
                            this.ScrollToBottom();
                        });
                    }
                })
                .catch(error => {
                    // Restore the attachments so the user doesn't have to re-select them, unless they've already attached something new
                    if(this.files.length == 0){
                        this.files = sentFiles;
                    }
                    ToastService.Error(error, 'OCURRIÓ UN ERROR AL PROCESAR LA RESPUESTA DEL ASISTENTE');
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    },
};
</script>

<style scoped>
/* ============================================================================
   AI Assistant — light working surface that speaks the redesigned navbar's
   language (raisin header, lavender accent, shared radii / motion). Tokens
   mirror the structure of the shared --DropdownPanel* set, but in light
   values because this is a reading + writing surface, not a dropdown.
   ============================================================================ */
.aiAssistantChatContainer {
    --ai-ink: #1f1f2c;
    --ai-ink-dim: rgba(31, 31, 44, 0.58);
    --ai-ink-faint: rgba(31, 31, 44, 0.4);
    --ai-accent: var(--LativoDarkPurple);
    --ai-accent-soft: var(--LativoLightPurple);
    --ai-surface: #f4f5f8;
    --ai-surface-2: #eceef4;
    --ai-line: rgba(31, 31, 44, 0.10);
    --ai-hover: rgba(31, 31, 44, 0.06);
    --ai-danger: #d92d20;
    --ai-ease: var(--DropdownPanelEase);

    flex-direction: column;
    max-height: 100vh;
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    background-color: white;
    border-radius: 16px;
    box-shadow: 0 24px 50px -18px rgba(9, 9, 14, 0.28), 0 2px 10px -4px rgba(9, 9, 14, 0.16);
    /* Entrance on open: the panel eases up into place instead of snapping in.
       Replays every time the draggable wrapper flips from hidden to shown. */
    animation: aiChatOpen 260ms var(--ai-ease) both;
}

@keyframes aiChatOpen {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to   { opacity: 1; transform: none; }
}

/* ---------- Shared icon button (navbar / notifications language) ---------- */
.aiIconBtn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background-color: transparent;
    border: none;
    border-radius: 10px;
    color: var(--ai-ink-dim);
    cursor: pointer;
    transition: background-color 160ms ease, color 160ms ease, transform 140ms var(--ai-ease);
    -webkit-tap-highlight-color: transparent;
}

.aiIconBtn i {
    font-size: 17px;
    color: inherit;
    margin: 0;
}

.aiIconBtn:active {
    transform: scale(0.95);
}

.aiIconBtn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.aiIconBtn:focus-visible {
    outline: 2px solid var(--ai-accent-soft);
    outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
    .aiIconBtn:not(:disabled):hover {
        background-color: var(--ai-hover);
        color: var(--ai-ink);
    }
}

/* ---------- Header (also the drag handle) ----------
   Light-gray bar, clearly distinct from the white conversation body. The
   assistant avatar is the single dark element that carries the brand mark.
   `grab` / `grabbing` (not `move`) name the interaction correctly, and the
   :active wash confirms the header heard the press the same way a button
   does — the panel itself already carries the motion via the drag lib, so
   the header only needs a quiet tactile cue, not a transform of its own. */
.header.aiHeader {
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
    padding: 10px 10px 10px 12px;
    background-color: var(--ai-surface-2);
    border-bottom: 1px solid var(--ai-line);
    cursor: grab;
    transition: background-color 120ms ease;
}

.header.aiHeader:active {
    cursor: grabbing;
    background-color: #e3e5ed;
    background-color: color-mix(in srgb, var(--ai-surface-2) 88%, var(--ai-ink) 12%);
}

/* The one dark element in the light header — a soft diagonal gradient gives
   it real depth instead of sitting as a flat, inert tile. */
.aiHeader__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: linear-gradient(135deg, #2a2a3a, var(--LativoDarkPurple));
    box-shadow: 0 4px 12px -6px rgba(31, 31, 44, 0.5);
}

.aiHeader__avatar img {
    width: 22px;
    height: 22px;
    transform: translateX(1px);
}

.aiHeader__title {
    flex: 1;
    min-width: 0;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 15px;
    letter-spacing: -0.01em;
    color: var(--ai-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Close button now rides on the light bar — inherits the dark ink + soft wash
   from .aiIconBtn; only its glyph size is tuned here. */
.aiHeader__close i {
    font-size: 15px;
}

/* ---------- Help toggle (floats over the top-left of the body) ---------- */
.helpButtonWrapper {
    position: relative;
    height: 0;
    z-index: 1000;
    pointer-events: none;
}

/* Round floating pill. Positioned with top/left (not transform) so the press
   scale can't override the offset and yank the button out from under the
   cursor — that was dropping every click that didn't land on the glyph. */
.helpButton {
    position: absolute;
    top: 10px;
    left: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: #ffffff;
    color: var(--ai-accent);
    box-shadow: 0 4px 14px -4px rgba(9, 9, 14, 0.25), 0 0 0 1px var(--ai-line);
    cursor: pointer;
    pointer-events: auto;
    /* Navbar button feel: quick colour wash + a firm press scale on the world's ease. */
    transition: background-color 160ms ease, color 160ms ease, transform 140ms var(--ai-ease);
    -webkit-tap-highlight-color: transparent;
}

.helpButton i {
    font-size: 16px;
    margin: 0;
    transition: transform 200ms var(--ai-ease);
}

.helpButton:active {
    transform: scale(0.9);
}

.helpButton:focus-visible {
    outline: 2px solid var(--ai-accent-soft);
    outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
    .helpButton:hover {
        background-color: var(--ai-accent);
        color: #ffffff;
    }
}

/* ---------- Body / conversation ---------- */
.body {
    overflow-y: auto;
    height: calc(100% - 100px);
    padding: 14px 12px 0;
    margin: 0;
    position: relative;
    scrollbar-width: thin;
    scrollbar-color: rgba(31, 31, 44, 0.18) transparent;
}

.body::-webkit-scrollbar {
    width: 8px;
}

.body::-webkit-scrollbar-thumb {
    background-color: rgba(31, 31, 44, 0.16);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
}

.body::-webkit-scrollbar-thumb:hover {
    background-color: rgba(31, 31, 44, 0.28);
}

.hiddenObserverDiv {
    height: 1px;
    width: 1px;
    display: block;
}

/* ---------- Footer / message bar ---------- */
.footer {
    position: absolute;
    bottom: 0;
    display: flex;
    width: 100%;
}

.messageBar {
    width: calc(100% - 20px);
    max-width: calc(100% - 20px);
    margin: 10px;
    background-color: var(--ai-surface);
    border: 1px solid var(--ai-line);
    border-radius: 18px;
    box-shadow: 0 2px 10px -6px rgba(9, 9, 14, 0.18);
    transition: border-color 160ms ease, box-shadow 160ms ease;
}

.messageBar:focus-within {
    border-color: rgba(31, 31, 44, 0.22);
    box-shadow: 0 4px 16px -8px rgba(9, 9, 14, 0.22);
}

.messageInputWrapper button {
    margin-top: auto;
}

.messageInputWrapper {
    display: flex;
    width: 100%;
    align-items: center;
}

.buttonWrapper {
    display: flex;
    padding: 5px;
}

.textAreaWrapper {
    flex: 1;
    display: flex;
    align-items: center;
}

.textAreaWrapper textarea {
    border: none;
    outline: none;
    width: 100%;
    max-width: 100%;
    resize: none;
    background-color: transparent;
    height: 40px;
    padding-top: 9px;
    padding-bottom: 8px;
    max-height: 80px;
    pointer-events: auto !important;
    font-size: 14px;
    color: var(--ai-ink);
    overflow-x: hidden;
    scrollbar-width: thin;
}

.textAreaWrapper textarea::placeholder {
    color: var(--ai-ink-faint);
}

/* Send button: the one filled, primary control in the bar. */
.postMessageButton {
    background-color: var(--ai-accent) !important;
    color: white;
}

.postMessageButton i {
    transition: transform 180ms var(--ai-ease);
}

@media (hover: hover) and (pointer: fine) {
    .postMessageButton:not(.disabled):hover {
        background-color: var(--LativoDarkPurpleHover) !important;
        color: white;
    }

    .postMessageButton:not(.disabled):hover i {
        transform: translateY(-2px);
    }
}

.postMessageButton.disabled,
.postMessageButton:disabled {
    background-color: rgba(31, 31, 44, 0.14) !important;
    color: rgba(31, 31, 44, 0.4);
    cursor: not-allowed;
}

/* ---------- Attachments ---------- */
.filesContainer {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 10px;
    overflow-x: auto;
    scrollbar-width: thin;
}

.filesContainer.empty {
    padding: 0px;
    height: 0px;
}

.filesContainer::-webkit-scrollbar {
    height: 8px;
}

.clearFilesButton {
    width: 30px;
    height: 30px;
    margin-right: 6px;
}

.clearFilesButton i {
    font-size: 14px;
}

@media (hover: hover) and (pointer: fine) {
    .clearFilesButton:not(:disabled):hover {
        background-color: var(--ai-accent-soft);
        color: var(--ai-accent);
    }
}

/* Same chip language as the attached-file chips inside AssistantMessage.vue
   (.aiMessage__fileChip) — the staged files in the composer and the files
   already sent in a message read as the same kind of object. */
.selectedFile {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 9px;
    min-height: 38px;
    width: max-content;
    font-size: 13px;
    color: var(--ai-ink-dim);
    margin-right: 8px;
    background-color: var(--ai-surface);
    border: 1px solid var(--ai-line);
    border-radius: 10px;
    white-space: nowrap;
}

.selectedFile__icon {
    flex-shrink: 0;
}

.selectedFile__name {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--ai-ink);
}

.removeFileButton {
    margin-left: 2px;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    border: none;
    border-radius: 7px;
    background-color: transparent;
    color: var(--ai-ink-dim);
    transition: background-color 160ms ease, color 160ms ease;
}

.removeFileButton i {
    font-size: 14px;
    margin-right: 0px;
}

@media (hover: hover) and (pointer: fine) {
    .removeFileButton:hover {
        background-color: var(--ai-accent-soft);
        color: var(--ai-accent);
    }
}

.warningsWrapper {
    padding-left: 12px;
    padding-top: 6px;
    font-size: 12px;
}

.warningsWrapper .required {
    color: var(--ai-danger);
    margin: 0;
}

/* ---------- Go-to-bottom ---------- */
.goToBottomBurronWrapper {
    position: absolute;
    background-color: transparent;
    pointer-events: none;
}

.goToBottomButton {
    background-color: var(--ai-accent);
    color: white;
    border-radius: 50%;
    width: 34px;
    height: 34px;
    margin-top: 10px;
    margin-bottom: 10px;
    border: none;
    cursor: pointer;
    pointer-events: auto;
    box-shadow: 0 6px 16px -6px rgba(9, 9, 14, 0.35);
    transition: background-color 160ms ease, transform 160ms var(--ai-ease);
}

.goToBottomButton i {
    margin-right: 0px;
}

.goToBottomButton:active {
    transform: scale(0.94);
}

.goToBottomButton:focus-visible {
    outline: 2px solid var(--ai-accent-soft);
    outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
    .goToBottomButton:hover {
        background-color: var(--LativoDarkPurpleHover);
        transform: translateY(2px);
    }
}

/* Fade transition for the go-to-bottom control. */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.18s ease;
}

.fade-enter, .fade-leave-to {
    opacity: 0;
}

/* ---------- Loaders ---------- */
.assistantLoadingWrapper {
    margin-bottom: 15px;
    height: min-content;
}

.assistantLoading {
    width: 22px !important;
    height: 22px !important;
    border-bottom-width: 2px !important;
    border-top-width: 2px !important;
    border-left-width: 2px !important;
    border-right-width: 2px !important;
    color: var(--ai-accent);
}

/* ---------- Instructions ---------- */
.body.instructions {
    padding: 16px 14px !important;
}

.body.instructions > div {
    margin-bottom: 14px;
}

.body.instructions > div:last-child {
    margin-bottom: 0px;
}

.instructionsBotRow {
    width: 100%;
    height: auto;
    margin-bottom: 16px !important;
}

.instructionsBotIconWrapper {
    border-radius: 18px;
    background-color: var(--LativoDarkPurple);
    width: 64px;
    height: 64px;
    box-shadow: 0 10px 26px -10px rgba(31, 31, 44, 0.5);
}

.instructionsBotIconWrapper img {
    width: 36px;
    height: 36px;
    transform: translateX(2px);
}

.instructionsCard {
    background-color: var(--ai-surface);
    border: 1px solid var(--ai-line);
    border-radius: 14px;
    padding: 13px 14px;
    color: rgb(60, 60, 60);
    font-size: 13.5px;
    line-height: 1.5;
}

.instructionsHeader {
    display: flex;
    align-items: center;
    gap: 9px;
    font-family: 'SwitzerMedium', sans-serif;
    font-weight: normal;
    margin-bottom: 9px;
    color: var(--LativoDarkPurple);
    font-size: 14px;
}

.instructionsHeader__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: 9px;
    background-color: rgba(31, 31, 44, 0.06);
    color: var(--ai-accent);
}

.instructionsHeader__icon i {
    font-size: 15px;
    color: inherit;
}

.instructionsHeader__icon i.bi-paperclip {
    display: inline-block;
    transform: rotate(-145deg);
}

/* ---------- Reduced motion ---------- */
@media (prefers-reduced-motion: reduce) {
    .aiIconBtn:active,
    .helpButton:active,
    .goToBottomButton:active,
    .goToBottomButton:hover,
    .postMessageButton:hover i {
        transform: none;
    }

    /* Open with a plain fade — no travel or scale. */
    .aiAssistantChatContainer {
        animation: aiChatFade 180ms ease both;
    }
}

@keyframes aiChatFade {
    from { opacity: 0; }
    to   { opacity: 1; }
}
</style>

<style>
/* Global on purpose: .loading-dots/.aiAssistantCentered are injected via
   v-html into AssistantMessage.vue, and .vdr/.handle belong to the
   vue-draggable-resizable wrapper rendered by Navbar.vue — none of these
   elements receive this component's scoped data-v attribute. */
.aiAssistantCentered {
    display: flex;
    justify-content: center;
    align-items: center;
}

.loading-dots {
    display: flex;
    justify-content: center;
    align-items: center;
}

.loading-dots div {
    width: 6px;
    height: 6px;
    margin: 0 3px;
    background-color: rgba(31, 31, 44, 0.4);
    border-radius: 50%;
    /* The one authored loop: three dots easing up and settling in sequence —
       the same "thinking" language as the notifications spinner, on the
       world's ease rather than an elastic bounce. */
    animation: aiTypingDot 1s var(--ai-ease) infinite;
}

.loading-dots div:nth-child(2) {
    animation-delay: 150ms;
}

.loading-dots div:nth-child(3) {
    animation-delay: 300ms;
}

@keyframes aiTypingDot {
    0%, 100% { transform: translateY(0); opacity: 0.35; }
    50%      { transform: translateY(-5px); opacity: 1; }
}

.vdr {
    border: none !important;
    position: fixed !important;
    pointer-events: auto !important;
}

.handle {
    border-radius: 50%;
    border: none !important;
}

@media (prefers-reduced-motion: reduce) {
    .loading-dots div {
        animation: aiTypingFade 1.2s ease-in-out infinite;
    }
}

@keyframes aiTypingFade {
    0%, 100% { opacity: 0.35; }
    50%      { opacity: 1; }
}
</style>
